import { API, Endpoints, unpackEndpoint } from "@brg/api"

const endpoint = unpackEndpoint({
    develop: 'https://api.burgdev.ru/ratings',
    staging: 'https://api.burgstg.ru/ratings',
    production: 'https://api.mediiia.ru/ratings',
    hseOnline: 'https://api.hsedesign.online/ratings',
})

const qualitiesEndpoint = unpackEndpoint({
    develop: 'https://api.burgdev.ru/qualities',
    staging: 'https://api.burgstg.ru/qualities',
    production: 'https://api.mediiia.ru/qualities',
    hseOnline: 'https://api.hsedesign.online/qualities',
})

export default function ratingsApi() {
    return {
        getLogs () {
            return API.request()
                .endpoint(endpoint)
                .url('/api/ratings/rating-rules')
                .addQuery('type', 0)
                .get()
                .send()
                .then(res => res.json())
        },
        getAllRatings () {
            return API.request()
                .endpoint(endpoint)
                .url('/api/ratings')
                .addQuery('ratingType', 33)
                .addQuery('limit', 1)
                .addQuery('intervalType', 0)
                .addQuery('onlyPositiveValues', 'true')
                .get()
                .send()
                .then(res => res.json())
        },
        async getRatingsByFilter (filter: {
            ratingType: number;
            intervalType: number;
            entityType: number;
            date?: string;
            page?: number;
            limit?: number;
            entityId?: string;
        }) {
            const request = API.request()
                .endpoint(endpoint)
                .url('/api/ratings')
                .addQuery('ratingType', filter.ratingType)
                .addQuery('intervalType', filter.intervalType)
                .addQuery('entityType', filter.entityType)
                .addQuery('page', filter.page ?? 1)
                .addQuery('limit', filter.limit ?? 10);

            if (filter.date) {
                request.addQuery('date', filter.date);
            }
            if (filter.entityId) {
                request.addQuery('entityId', filter.entityId);
            }

            return await request.get().send().then(res => res.json());
        },
        async getRatingsForPeriod (
            apiEndpoint: string,
            url: string,
            filter: {
                ratingType: number;
                intervalType: number;
                date: string;
                page?: number;
                limit?: number;
            },
        ) {
            return await API.request()
                .endpoint(apiEndpoint)
                .url(url)
                .addQuery('ratingType', filter.ratingType)
                .addQuery('intervalType', filter.intervalType)
                .addQuery('date', filter.date)
                .addQuery('page', filter.page ?? 1)
                .addQuery('limit', filter.limit ?? 10)
                .get()
                .send()
                .then(res => res.json());
        },
        getContextRatings (filter: {
            ratingType: number;
            intervalType: number;
            date: string;
            page?: number;
            limit?: number;
        }) {
            return this.getRatingsForPeriod(endpoint, '/api/ratings/tag-contexts', filter);
        },
        getProjectRatings (filter: {
            ratingType: number;
            intervalType: number;
            date: string;
            page?: number;
            limit?: number;
        }) {
            return API.request()
                .endpoint(Endpoints.geograffee)
                .url('/api/Project/RatingsForPeriod')
                .addQuery('date', filter.date)
                .addQuery('type', filter.ratingType)
                .addQuery('timeIntervalType', filter.intervalType)
                .addQuery('page', filter.page ?? 1)
                .addQuery('limit', filter.limit ?? 10)
                .get()
                .send()
                .then(res => res.json());
        },
        getDesignerRatings (filter: {
            ratingType: number;
            intervalType: number;
            date: string;
            page?: number;
            limit?: number;
        }) {
            return API.request()
                .endpoint(Endpoints.geograffee)
                .url('/api/Designer/RatingsForPeriod')
                .addQuery('date', filter.date)
                .addQuery('type', filter.ratingType)
                .addQuery('timeIntervalType', filter.intervalType)
                .addQuery('page', filter.page ?? 1)
                .addQuery('limit', filter.limit ?? 10)
                .get()
                .send()
                .then(res => res.json());
        },
        fetchRatingsByPeriod (entityType: string, filter: any) {
            if (entityType === 'projects_ratings') {
                return this.getProjectRatings(filter);
            } else if (entityType === 'authors_ratings') {
                return this.getDesignerRatings(filter);
            }
        },
        getTagRatings (filter: {
            ratingType: number;
            intervalType: number;
            date: string;
            page?: number;
            limit?: number;
        }) {
            return API.request()
                .endpoint(Endpoints.tagsMnePw)
                .url('/api/Tags/RatingsForPeriod')
                .addQuery('date', filter.date)
                .addQuery('type', filter.ratingType)
                .addQuery('timeIntervalType', filter.intervalType)
                .addQuery('page', filter.page ?? 1)
                .addQuery('limit', filter.limit ?? 10)
                .get()
                .send()
                .then(res => res.json());
        },
        getUserRatings (userId = '') {
            return API.request()
                .endpoint(endpoint)
                .url('/api/ratings')
                .addQuery('ratingType', 33)
                .addQuery('limit', 1)
                .addQuery('intervalType', 0)
                .addQuery('onlyPositiveValues', 'true')
                .addQuery('entityId', userId)
                .get()
                .send()
                .then(res => res.json())
        },
        saveRatingRules (ratingRules: any) {
            return API.request()
                .endpoint(endpoint)
                .url('/api/ratings/rating-rules-update')
                .body(ratingRules)
                .post()
                .send()
        },
        getAllMetrics () {
            return API.request()
                .endpoint(endpoint)
                .url('/api/ratings/Metrics')
                .addQuery('date', new Date().toDateString())
                .get()
                .send()
                .then(res => res.json())
        },
        getAllMetricsNew () {
            return API.request()
                .endpoint(endpoint)
                .url('/api/ratings/metric-packs')
                .get()
                .send()
                .then(res => res.json())
        },
        updateMetrics (metricsList: any[]) {
            return API.request()
                .endpoint(endpoint)
                .url('/api/ratings/metric-packs-update')
                .body({metricPacks: metricsList})
                .post()
                .send()
        },
        getUsersWithCustomLikeWeight () {
            return API.request()
                .endpoint(qualitiesEndpoint)
                .url('/api/accountweight/users-custom-weight')
                .get()
                .send()
                .then(res => res.json())
        },
        getUserMetrics (userId: string, date = new Date().toDateString(), intervalType = 3, needStudentsPoints?: boolean, disciplineOwnerType?: number) {
            return API.request()
                .endpoint(endpoint)
                    .url('/api/ratings/user-rating-statistics')
                    .addQuery('userId', userId)
                    .addQuery('disciplineOwnerTypes', (disciplineOwnerType || disciplineOwnerType === 0) ? disciplineOwnerType : [0, 1, 2])
                    .addQuery('date', date)
                    .addQuery('intervalType', intervalType)
                    .addQuery('needStudentsPoints', needStudentsPoints? 'true' : 'false')
                    .get()
                    .send()
                    .then(res => res.json())
        },
        getUserLikeWeight (userId: string) {
            return API.request()
                .endpoint(qualitiesEndpoint)
                    .url('/api/accountweight/GetWeight')
                    .addQuery('accountId', userId)
                    .get()
                    .send()
                    .then(res => res.json())
        },
        getMetricsTimePeriods () {
            return API.request()
                .endpoint(endpoint)
                    .url('/api/ratings/periods')
                    .addQuery('disciplineOwnerType', 2)
                    .get()
                    .send()
                    .then(res => res.json())
        },
        getUsersWithoutRatingsCount () {
            return API.request()
                .endpoint(endpoint)
                    .url('/api/ratings/users-without-rating')
                    .get()
                    .send()
                    .then(res => res.json())
        }
    }
}import { API, Endpoints, unpackEndpoint } from "@brg/api"

const endpoint = unpackEndpoint({
    develop: 'https://api.burgdev.ru/ratings',
    staging: 'https://api.burgstg.ru/ratings',
    production: 'https://api.mediiia.ru/ratings',
    hseOnline: 'https://api.hsedesign.online/ratings',
})

const qualitiesEndpoint = unpackEndpoint({
    develop: 'https://api.burgdev.ru/qualities',
    staging: 'https://api.burgstg.ru/qualities',
    production: 'https://api.mediiia.ru/qualities',
    hseOnline: 'https://api.hsedesign.online/qualities',
})

export default function ratingsApi() {
    return {
        getLogs () {
            return API.request()
                .endpoint(endpoint)
                .url('/api/ratings/rating-rules')
                .addQuery('type', 0)
                .get()
                .send()
                .then(res => res.json())
        },
        getAllRatings () {
            return API.request()
                .endpoint(endpoint)
                .url('/api/ratings')
                .addQuery('ratingType', 33)
                .addQuery('limit', 1)
                .addQuery('intervalType', 0)
                .addQuery('onlyPositiveValues', 'true')
                .get()
                .send()
                .then(res => res.json())
        },
        async getRatingsByFilter (filter: {
            ratingType: number;
            intervalType: number;
            entityType: number;
            date?: string;
            page?: number;
            limit?: number;
            entityId?: string;
        }) {
            const request = API.request()
                .endpoint(endpoint)
                .url('/api/ratings')
                .addQuery('ratingType', filter.ratingType)
                .addQuery('intervalType', filter.intervalType)
                .addQuery('entityType', filter.entityType)
                .addQuery('page', filter.page ?? 1)
                .addQuery('limit', filter.limit ?? 10);

            if (filter.date) {
                request.addQuery('date', filter.date);
            }
            if (filter.entityId) {
                request.addQuery('entityId', filter.entityId);
            }

            return await request.get().send().then(res => res.json());
        },
        async getRatingsForPeriod (
            apiEndpoint: string,
            url: string,
            filter: {
                ratingType: number;
                intervalType: number;
                date: string;
                page?: number;
                limit?: number;
            },
        ) {
            return await API.request()
                .endpoint(apiEndpoint)
                .url(url)
                .addQuery('ratingType', filter.ratingType)
                .addQuery('intervalType', filter.intervalType)
                .addQuery('date', filter.date)
                .addQuery('page', filter.page ?? 1)
                .addQuery('limit', filter.limit ?? 10)
                .get()
                .send()
                .then(res => res.json());
        },
        getContextRatings (filter: {
            ratingType: number;
            intervalType: number;
            date: string;
            page?: number;
            limit?: number;
        }) {
            return this.getRatingsForPeriod(endpoint, '/api/ratings/tag-contexts', filter);
        },
        getProjectRatings (filter: {
            ratingType: number;
            intervalType: number;
            date: string;
            page?: number;
            limit?: number;
        }) {
            return API.request()
                .endpoint(Endpoints.geograffee)
                .url('/api/Project/RatingsForPeriod')
                .addQuery('date', filter.date)
                .addQuery('type', filter.ratingType)
                .addQuery('timeIntervalType', filter.intervalType)
                .addQuery('page', filter.page ?? 1)
                .addQuery('limit', filter.limit ?? 10)
                .get()
                .send()
                .then(res => res.json());
        },
        getDesignerRatings (filter: {
            ratingType: number;
            intervalType: number;
            date: string;
            page?: number;
            limit?: number;
        }) {
            return API.request()
                .endpoint(Endpoints.geograffee)
                .url('/api/Designer/RatingsForPeriod')
                .addQuery('date', filter.date)
                .addQuery('type', filter.ratingType)
                .addQuery('timeIntervalType', filter.intervalType)
                .addQuery('page', filter.page ?? 1)
                .addQuery('limit', filter.limit ?? 10)
                .get()
                .send()
                .then(res => res.json());
        },
        fetchRatingsByPeriod (entityType: string, filter: any) {
            if (entityType === 'projects_ratings') {
                return this.getProjectRatings(filter);
            } else if (entityType === 'authors_ratings') {
                return this.getDesignerRatings(filter);
            }
        },
        getTagRatings (filter: {
            ratingType: number;
            intervalType: number;
            date: string;
            page?: number;
            limit?: number;
        }) {
            return API.request()
                .endpoint(Endpoints.tagsMnePw)
                .url('/api/Tags/RatingsForPeriod')
                .addQuery('date', filter.date)
                .addQuery('type', filter.ratingType)
                .addQuery('timeIntervalType', filter.intervalType)
                .addQuery('page', filter.page ?? 1)
                .addQuery('limit', filter.limit ?? 10)
                .get()
                .send()
                .then(res => res.json());
        },
        getUserRatings (userId = '') {
            return API.request()
                .endpoint(endpoint)
                .url('/api/ratings')
                .addQuery('ratingType', 33)
                .addQuery('limit', 1)
                .addQuery('intervalType', 0)
                .addQuery('onlyPositiveValues', 'true')
                .addQuery('entityId', userId)
                .get()
                .send()
                .then(res => res.json())
        },
        saveRatingRules (ratingRules: any) {
            return API.request()
                .endpoint(endpoint)
                .url('/api/ratings/rating-rules-update')
                .body(ratingRules)
                .post()
                .send()
        },
        getAllMetrics () {
            return API.request()
                .endpoint(endpoint)
                .url('/api/ratings/Metrics')
                .addQuery('date', new Date().toDateString())
                .get()
                .send()
                .then(res => res.json())
        },
        getAllMetricsNew () {
            return API.request()
                .endpoint(endpoint)
                .url('/api/ratings/metric-packs')
                .get()
                .send()
                .then(res => res.json())
        },
        updateMetrics (metricsList: any[]) {
            return API.request()
                .endpoint(endpoint)
                .url('/api/ratings/metric-packs-update')
                .body({metricPacks: metricsList})
                .post()
                .send()
        },
        getUsersWithCustomLikeWeight () {
            return API.request()
                .endpoint(qualitiesEndpoint)
                .url('/api/accountweight/users-custom-weight')
                .get()
                .send()
                .then(res => res.json())
        },
        getUserMetrics (userId: string, date = new Date().toDateString(), intervalType = 3, needStudentsPoints?: boolean, disciplineOwnerType?: number) {
            return API.request()
                .endpoint(endpoint)
                    .url('/api/ratings/user-rating-statistics')
                    .addQuery('userId', userId)
                    .addQuery('disciplineOwnerTypes', (disciplineOwnerType || disciplineOwnerType === 0) ? disciplineOwnerType : [0, 1, 2])
                    .addQuery('date', date)
                    .addQuery('intervalType', intervalType)
                    .addQuery('needStudentsPoints', needStudentsPoints? 'true' : 'false')
                    .get()
                    .send()
                    .then(res => res.json())
        },
        getUserLikeWeight (userId: string) {
            return API.request()
                .endpoint(qualitiesEndpoint)
                    .url('/api/accountweight/GetWeight')
                    .addQuery('accountId', userId)
                    .get()
                    .send()
                    .then(res => res.json())
        },
        getMetricsTimePeriods () {
            return API.request()
                .endpoint(endpoint)
                    .url('/api/ratings/periods')
                    .addQuery('disciplineOwnerType', 2)
                    .get()
                    .send()
                    .then(res => res.json())
        },
        getUsersWithoutRatingsCount () {
            return API.request()
                .endpoint(endpoint)
                    .url('/api/ratings/users-without-rating')
                    .get()
                    .send()
                    .then(res => res.json())
        }
    }
}